import React, { useEffect, useRef, useState } from "react";
import MessageBubble from "./MessageBubble.jsx";
import TypingIndicator from "./TypingIndicator.jsx";
import AgentCard from "./AgentCard.jsx";
import ConversationHistory from "../ConversationHistory.jsx";
import "./styles.css";

const API_BASE =
  process.env.REACT_APP_CHAT_API_BASE || "http://localhost:4000";

const TENANTS = [
  { id: "restaurant-default", label: "Restaurant (default)" },
  { id: "foodtruck-demo", label: "Food Truck Demo" },
  { id: "gym-demo", label: "Gym Demo" },
  { id: "icebath-demo", label: "Icebath Demo" },
];

const getTenantFromURL = () => {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const t = params.get("clientId") || params.get("tenantId");
  return t ? t.trim() : null;
};

const createBotWelcomeMessage = () => ({
  id: `bot-welcome-${Date.now()}`,
  role: "bot",
  text: "Hi there — I can help with reservations. What date would you like?",
  timestamp: new Date().toISOString(),
});

const HISTORY_STORAGE_KEY = "chatbotics-local-history";
const HISTORY_LIMIT = 20;
const SYSTEM_MESSAGE_KEY = "chatbotics-system-message";

const loadSavedConversations = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const loadSystemMessage = () => {
  if (typeof window === "undefined") return "";
  try {
    return localStorage.getItem(SYSTEM_MESSAGE_KEY) || "";
  } catch {
    return "";
  }
};

export default function ChatWindow() {
  const [messages, setMessages] = useState([createBotWelcomeMessage()]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const [lastUserMessageText, setLastUserMessageText] = useState(null);
  const initialTenant = getTenantFromURL() || "restaurant-default";
  const [tenants, setTenants] = useState(TENANTS);
  const [tenant, setTenant] = useState(initialTenant);
  const [savedConversations, setSavedConversations] = useState(
    loadSavedConversations
  );
  const [selectedConversationId, setSelectedConversationId] = useState(
    () => loadSavedConversations()[0]?.id || null
  );
  const [systemMessage, setSystemMessage] = useState(loadSystemMessage);

  const messagesEndRef = useRef(null);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Persist local history
  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(
      HISTORY_STORAGE_KEY,
      JSON.stringify(savedConversations)
    );
  }, [savedConversations]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(SYSTEM_MESSAGE_KEY, systemMessage);
  }, [systemMessage]);

  // On mount, allow tenant override via URL param and add to dropdown if missing
  useEffect(() => {
    const urlTenant = getTenantFromURL();
    if (urlTenant) {
      setTenant(urlTenant);
      setTenants((prev) =>
        prev.some((t) => t.id === urlTenant)
          ? prev
          : [...prev, { id: urlTenant, label: urlTenant }]
      );
    }
  }, []);

  const archiveConversation = (reason = "manual-save") => {
    const hasUserMessage = messages.some((msg) => msg.role === "user");
    if (!hasUserMessage) return null;

    const archived = {
      id: `${tenant}-${Date.now()}`,
      tenant,
      savedAt: new Date().toISOString(),
      reason,
      messages: messages.map((msg) => ({ ...msg })),
    };

    setSavedConversations((prev) => {
      const next = [archived, ...prev].slice(0, HISTORY_LIMIT);
      return next;
    });

    return archived.id;
  };

  const startFreshConversation = () => {
    setMessages([createBotWelcomeMessage()]);
    setError(null);
    setIsTyping(false);
    setIsSending(false);
    setInput("");
    setLastUserMessageText(null);
  };

  const sendMessage = async (overrideText = null, options = {}) => {
    // Ignore click events passed in from button onClick
    if (overrideText && typeof overrideText === "object" && typeof overrideText.preventDefault === "function") {
      overrideText.preventDefault();
      overrideText = null;
    }

    const raw = overrideText ?? input;
    const messageText =
      typeof raw === "string" ? raw.trim() : String(raw || "").trim();
    if (!messageText) return;
    if (isSending) return;

    if (!options.skipUserBubble) {
      const userMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        text: messageText,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setInput("");
    }

    setLastUserMessageText(messageText);
    setIsSending(true);
    setIsTyping(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: messageText,
          clientId: tenant, // <-- SELECTED TENANT HERE
          systemMessage,
        }),
      });

      const data = await res.json();
      setIsTyping(false);

      if (data.success && data.reply) {
        const botMessage = {
          id: `bot-${Date.now()}`,
          role: "bot",
          text: data.reply,
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, botMessage]);
      } else {
        setError("⚠️ Error: Could not reach the server.");
      }
    } catch (err) {
      setIsTyping(false);
      setError("⚠️ Error: Could not reach the server.");
    }

    setIsSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetChat = () => {
    const archivedId = archiveConversation("reset-chat");
    if (archivedId) {
      setSelectedConversationId(archivedId);
    }
    startFreshConversation();
  };

  const handleTenantChange = (nextTenant) => {
    const archivedId = archiveConversation("tenant-switch");
    if (archivedId) {
      setSelectedConversationId(archivedId);
    }
    setTenant(nextTenant);
    startFreshConversation();
  };

  const handleSaveConversation = () => {
    const archivedId = archiveConversation("manual-save");
    if (archivedId) {
      setSelectedConversationId(archivedId);
    }
  };

  const handleClearHistory = () => {
    setSavedConversations([]);
    setSelectedConversationId(null);
    if (typeof window !== "undefined") {
      localStorage.removeItem(HISTORY_STORAGE_KEY);
    }
  };

  const clearSystemMessage = () => setSystemMessage("");

  const retryLastMessage = () => {
    if (!lastUserMessageText) {
      setError("No previous message to retry.");
      return;
    }
    sendMessage(lastUserMessageText, { skipUserBubble: true });
  };

  const statusLabel = error
    ? "Offline • tap retry"
    : isSending
    ? "Sending…"
    : isTyping
    ? "Agent typing…"
    : "Ready";

  return (
    <div className="chat-shell">
      <div className="chat-container">
        <div className="chat-header">
          <div>
            <p className="chat-eyebrow">Multi-tenant demo</p>
            <h2>Chat Demo</h2>
            <span className="tenant-pill">
              Tenant: {tenant}
              {getTenantFromURL() && <span className="tenant-pill__badge">from URL</span>}
            </span>
            <span className={`status-pill ${error ? "status-pill--error" : ""}`}>
              {statusLabel}
            </span>
          </div>

          <div className="tenant-wrapper">
            <select
            className="tenant-select"
            value={tenant}
            onChange={(e) => handleTenantChange(e.target.value)}
          >
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>

            <button className="ghost-btn" onClick={handleSaveConversation}>
              Save to History
            </button>

            <button className="reset-btn" onClick={resetChat}>
              Reset Chat
            </button>
          </div>
        </div>

        <div className="system-box">
          <div className="system-box__header">
            <div>
              <p className="system-eyebrow">System message (local test)</p>
              <p className="system-note">
                Sent with each request; stored only in this browser.
              </p>
            </div>
            <div className="system-actions">
              <span className="system-counter">
                {systemMessage.length}/400
              </span>
              <button
                className="link-btn system-clear"
                type="button"
                onClick={clearSystemMessage}
                disabled={!systemMessage.length}
              >
                Clear
              </button>
            </div>
          </div>
          <textarea
            className="system-textarea"
            placeholder="e.g., Keep answers under two sentences and always confirm allergy info."
            value={systemMessage}
            maxLength={400}
            onChange={(e) => setSystemMessage(e.target.value)}
          />
        </div>

        <AgentCard tenant={tenant} />

        <div className="messages-container">
          {messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              role={msg.role}
              text={msg.text}
              timestamp={msg.timestamp}
            />
          ))}

          {isTyping && <TypingIndicator />}

          {error && (
            <div className="error-banner">
              <div>{error}</div>
              <div className="error-actions">
                <button
                  className="ghost-btn ghost-btn--danger"
                  type="button"
                  onClick={() => setError(null)}
                >
                  Dismiss
                </button>
                <button
                  className="ghost-btn"
                  type="button"
                  onClick={retryLastMessage}
                  disabled={!lastUserMessageText}
                >
                  Retry last
                </button>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="input-container">
          <input
            type="text"
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button onClick={() => sendMessage()} disabled={isSending}>
            {isSending ? (
              <span className="btn-loading">
                <span className="dot" />
                <span className="dot" />
                <span className="dot" />
                Sending...
              </span>
            ) : (
              "Send"
            )}
          </button>
        </div>
      </div>

      <ConversationHistory
        conversations={savedConversations}
        onSelectConversation={setSelectedConversationId}
        selectedId={selectedConversationId}
        onClear={handleClearHistory}
        onSave={handleSaveConversation}
        activeTenant={tenant}
        historyLimit={HISTORY_LIMIT}
      />
    </div>
  );
}
